import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { motion, useInView } from "framer-motion";

export function AboutSection() {
  const { t, i18n } = useTranslation();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-20% 0px -10% 0px" });
  const isZh = i18n.language?.startsWith("zh");

  return (
    <section
      ref={ref}
      id="about"
      data-testid="about-section"
      className="relative w-full py-28 sm:py-36"
    >
      <div className="relative z-10 w-full max-w-[1120px] mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="h-[1px] w-8 bg-[var(--gold-400)]/60" />
          <span className="text-[11px] tracking-[0.3em] uppercase font-mono-brand text-[var(--paper-100)]/80">
            {t("about.eyebrow")}
          </span>
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1.0, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className={[
            "max-w-[920px] leading-[1.1] tracking-[-0.02em]",
            "text-[var(--paper-50)] text-shadow-depth text-4xl sm:text-5xl lg:text-[60px]",
            isZh ? "font-display-cn" : "font-display-en",
          ].join(" ")}
          data-testid="about-title"
        >
          {t("about.title")}
        </motion.h2>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1.0, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className={[
              "lg:col-span-7 space-y-6 text-[var(--paper-100)]/90 text-base sm:text-lg leading-[1.85]",
              isZh ? "font-body-cn" : "font-body-en",
            ].join(" ")}
          >
            <p>{t("about.body1")}</p>
            <p className="text-[var(--paper-100)]/75">{t("about.body2")}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1.0, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5 lg:pl-8"
          >
            <div className="glass rounded-2xl p-6 sm:p-8 space-y-6">
              <StatRow
                label={t("about.statReleases")}
                value={t("about.statReleasesValue")}
                accent
              />
              <div className="h-[1px] bg-white/5" />
              <StatRow
                label={t("about.statArtists")}
                value={t("about.statArtistsValue")}
              />
              <div className="h-[1px] bg-white/5" />
              <StatRow
                label={t("about.statMarkets")}
                value={t("about.statMarketsValue")}
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function StatRow({ label, value, accent }) {
  return (
    <div className="flex items-end justify-between gap-6">
      <span className="text-[11px] tracking-[0.22em] uppercase font-mono-brand text-[var(--paper-100)]/75">
        {label}
      </span>
      <span
        className={[
          "font-display-en text-4xl sm:text-5xl tracking-[-0.02em] leading-none",
          accent ? "gold-shimmer-text" : "text-[var(--paper-50)] text-shadow-depth",
        ].join(" ")}
        style={{ fontWeight: 800 }}
      >
        {value}
      </span>
    </div>
  );
}
